import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Stack,
    InputAdornment,
    useTheme,
    alpha,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    Science as ScienceIcon,
    Speed as SpeedIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    Thermostat as ThermostatIcon,
    Opacity as WaterDropIcon,
    Business as BusinessIcon,
    Inventory2 as Inventory2Icon,
    GetApp as GetAppIcon,
    Close as CloseIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Group as GroupIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import { useNavigate } from 'react-router-dom';
import AreaCheckList from './production-line-log-components/AreaCheckList';
import SupplyLog from './production-line-log-components/SupplyLog';
import YieldLog from './production-line-log-components/YieldLog';
import SignatureLog from './production-line-log-components/SignatureLog';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`production-line-log-tabpanel-${index}`}
            aria-labelledby={`production-line-log-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 4, backgroundColor: 'white' }}>{children}</Box>}
        </div>
    );
}

function NavigationFooter({ currentStep, totalSteps, onStepChange, nextStepLabel = "Continue to Next Step", scrollRef, allStepsCompleted = false }) {
    const theme = useTheme();
    const isLastStep = currentStep === totalSteps - 1;
    const isFirstStep = currentStep === 0;
    const showWorkflowComplete = isLastStep && allStepsCompleted;

    const handleNext = () => {
        if (!isLastStep) {
            if (scrollRef && scrollRef.current) {
                scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            onStepChange(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (!isFirstStep) {
            if (scrollRef && scrollRef.current) {
                scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            onStepChange(currentStep - 1);
        }
    };

    return (
        <Box sx={{ 
            mt: 6, 
            pt: 4, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: `linear-gradient(180deg, transparent 0%, ${alpha(theme.palette.grey[50], 0.8)} 100%)`,
            mx: -4,
            px: 4,
            pb: 4
        }}>
            <Box>
                {!isFirstStep && (
                    <Button
                        variant="outlined"
                        onClick={handlePrevious}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                        }}
                    >
                        ← Previous Step
                    </Button>
                )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Step {currentStep + 1} of {totalSteps}
                </Typography>
                
                {!isLastStep && (
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            color: 'white',
                            fontWeight: 500,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                transform: 'translateY(-1px)',
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    >
                        {nextStepLabel} →
                    </Button>
                )}
                
                {showWorkflowComplete && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        backgroundColor: theme.palette.success.main,
                        borderRadius: 3,
                        px: 4,
                        py: 2.5,
                        boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.3)}`,
                        border: `1px solid ${theme.palette.success.dark}`
                    }}>
                        <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
                        <Box>
                            <Typography sx={{
                                color: 'white',
                                fontWeight: 500,
                                fontSize: '1rem',
                                lineHeight: 1
                            }}>
                                Workflow Complete
                            </Typography>
                            <Typography sx={{
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: '0.75rem',
                                fontWeight: 500
                            }}>
                                All steps finished successfully
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function ProcessNavigation({ currentStep, onStepChange, steps, scrollRef, workflowProgress }) {
    const theme = useTheme();
    const [animatingStep, setAnimatingStep] = useState(null);
    
    const handleStepClick = (stepId) => {
        setAnimatingStep(stepId);
        
        if (scrollRef && scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        setTimeout(() => {
            onStepChange(stepId);
            setAnimatingStep(null);
        }, 200);
    };

    const completedCount = steps.filter(s => s.completed).length;
    const progressPercentage = workflowProgress || (completedCount / steps.length) * 100;

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Progress Header */}
            <Box sx={{ mb: 3, textAlign: 'center', flexShrink: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                    Workflow Progress
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.success.main, lineHeight: 1 }}>
                        {Math.round(progressPercentage)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {completedCount} of {steps.length} complete
                    </Typography>
                </Box>
                
                {/* Vertical Progress Bar */}
                <Box sx={{ 
                    width: 8, 
                    height: 60,
                    backgroundColor: alpha(theme.palette.grey[300], 0.5), 
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    mx: 'auto'
                }}>
                    <Box sx={{
                        width: '100%',
                        height: `${progressPercentage}%`,
                        background: `linear-gradient(180deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                        transition: 'height 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: 4,
                        position: 'absolute',
                        bottom: 0
                    }} />
                </Box>
            </Box>

            {/* Vertical Step List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {steps.map((step, index) => {
                    const isActive = currentStep === step.id;
                    const isCompleted = step.completed;
                    const isAnimating = animatingStep === step.id;
                    
                    return (
                        <Box key={step.id} sx={{ position: 'relative' }}>
                            <Card
                                onClick={() => handleStepClick(step.id)}
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: 3,
                                    border: `1px solid ${
                                        isCompleted ? theme.palette.success.main : 
                                        isActive ? theme.palette.primary.main : theme.palette.grey[400]
                                    }`,
                                    backgroundColor: isCompleted ? alpha(theme.palette.success.main, 0.1) : 
                                                   isActive ? alpha(theme.palette.primary.main, 0.05) : 'white',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: isAnimating ? 'scale(0.95)' : 
                                              isActive ? 'translateX(4px)' : 'translateX(0px)',
                                    boxShadow: isActive ? `0 4px 16px ${alpha(theme.palette.primary.main, 0.12)}` :
                                               isCompleted ? `0 2px 8px ${alpha(theme.palette.success.main, 0.2)}` :
                                               `0 1px 4px ${alpha(theme.palette.grey[400], 0.08)}`,
                                    '&:hover': {
                                        transform: isAnimating ? 'scale(0.95)' : 'translateX(6px)',
                                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                                        borderColor: isCompleted ? theme.palette.success.main : theme.palette.primary.main
                                    },
                                    '&:active': {
                                        transform: 'scale(0.98) translateX(2px)'
                                    }
                                }}
                            >
                                {/* Active Indicator */}
                                {isActive && !isCompleted && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: -2,
                                        bottom: -2,
                                        left: -2,
                                        width: 4,
                                        background: `linear-gradient(180deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
                                        borderRadius: '4px 0 0 4px'
                                    }} />
                                )}

                                <CardContent sx={{ 
                                    p: 2, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2.5
                                }}>
                                    {/* Icon */}
                                    <Box sx={{ 
                                        fontSize: 24, 
                                        color: isCompleted ? theme.palette.success.main : 
                                               isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                        transition: 'all 0.3s ease',
                                        minWidth: 24
                                    }}>
                                        {isCompleted ? <CheckCircleIcon sx={{ fontSize: 24 }} /> : step.icon}
                                    </Box>

                                    <Box sx={{ flex: 1 }}>
                                        {/* Label */}
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                fontWeight: 500,
                                                color: isCompleted ? theme.palette.success.main : 
                                                       isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                                fontSize: '0.875rem',
                                                lineHeight: 1.2,
                                                mb: 0.5
                                            }}
                                        >
                                            {step.label}
                                        </Typography>

                                        {/* Status */}
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: isCompleted ? theme.palette.success.main : 
                                                       isActive ? theme.palette.warning.main : theme.palette.text.disabled,
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            {isCompleted ? '✓ Complete' : 
                                             isActive ? '● Active' : 'Pending'}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Connecting Line */}
                            {index < steps.length - 1 && (
                                <Box sx={{
                                    position: 'absolute',
                                    bottom: -12,
                                    left: 13,
                                    width: 2,
                                    height: 12,
                                    backgroundColor: isCompleted ? theme.palette.success.main : alpha(theme.palette.grey[400], 0.5),
                                    transition: 'all 0.3s ease'
                                }} />
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

const ProductionLineLogs = ({ 
  lineProductionId, 
  lineProductionData, 
  onProductionCompletion, 
  user 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [lineTimeFormat, setLineTimeFormat] = useState('12hour');
  const [tabValue, setTabValue] = useState(0);
  
  // Get specific permissions for each log component
  const canViewOverview = hasPermission('production.line.log.overview.view', 'read');
  const canViewAreaChecklist = hasPermission('production.line.log.checklist.view', 'read');
  const canViewSupplyLog = hasPermission('production.line.log.supply.view', 'read');
  const canViewYieldLog = hasPermission('production.line.log.yield.view', 'read');
  const canViewSignatures = hasPermission('production.line.log.signatures.view', 'read');
  const [lineLog, setLineLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lineLogsRef = useRef(null);
  const checkingYieldRef = useRef(false); // Prevent concurrent yield checks
  const yieldWarningShownRef = useRef(false); // Track if we've warned about 406 errors
  
  // State for completion tracking
  const [workflowProgress, setWorkflowProgress] = useState(0);
  const [yieldSummaryCompleted, setYieldSummaryCompleted] = useState(false);
  const [completionStatus, setCompletionStatus] = useState({
    overview: false,
    areaChecklist: false,
    supplyLog: false,
    summaryLog: false,
    signatureLog: false
  });

  // State for signatures
  const [signatures, setSignatures] = useState([]);

  const [operators, setOperators] = useState([
    {
      id: null,
      operator_name: '',
      operator_signature: '',
      operator_role: 'Operator',
      is_primary: true,
      shift_start_time: dayjs(),
      shift_end_time: null,
      is_active: true
    }
  ]);

  // Area checklist state
  const [areaChecklist, setAreaChecklist] = useState(null);

  // Supply log state
  const [supplies, setSupplies] = useState([]);
  const [lineItems, setLineItems] = useState([]);

  // Load user profile for time format preference
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        if (!user?.id) return;
        
        const { data, error } = await supabase
          .from('profiles')
          .select('line_production_time_format_preference')
          .eq('id', user.id)
          .single();
          
        if (data && !error) {
          setLineTimeFormat(data.line_production_time_format_preference || '12hour');
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
        setLineTimeFormat('12hour');
      }
    };
    
    loadUserProfile();
  }, [user?.id]);

  // Load existing production line log data
  useEffect(() => {
    if (lineProductionId) {
      loadProductionLineLogData();
      loadLineItems();
      loadSignatures(); // Load signatures immediately when component mounts
    }
  }, [lineProductionId]);

  // Function to load signatures
  const loadSignatures = async () => {
    if (!lineProductionId) return;

    try {
      const { data, error } = await supabase
        .from('production_line_log_signatures')
        .select('*')
        .eq('line_production_id', lineProductionId)
        .order('created_at', { ascending: true });

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading signatures:', error);
        return;
      }

      if (data) {
        setSignatures(data);
      }
    } catch (error) {
      console.error('Error loading signatures:', error);
    }
  };

  // Load supplies when line log is available
  useEffect(() => {
    if (lineLog?.id) {
      loadSupplies(lineLog.id);
    }
  }, [lineLog?.id]);

  // Load area checklist when line log changes
  useEffect(() => {
    if (lineLog?.id) {
      loadAreaChecklist(lineLog.id);
    }
  }, [lineLog?.id]);

  const loadOperators = async (lineLogId) => {
    if (!lineLogId) return;
    
    try {
      const { data, error } = await supabase
        .from('production_line_operators')
        .select('*')
        .eq('line_log_id', lineLogId)
        .eq('is_active', true)
        .order('is_primary', { ascending: false });

      if (error) {
        console.error('Error loading operators:', error);
        return;
      }

      if (data && data.length > 0) {
        setOperators(data.map(op => ({
          ...op,
          shift_start_time: op.shift_start_time ? dayjs(op.shift_start_time) : dayjs(),
          shift_end_time: op.shift_end_time ? dayjs(op.shift_end_time) : null
        })));
      }
    } catch (error) {
      console.error('Error loading operators:', error);
    }
  };

  const loadSupplies = async (lineLogId) => {
    if (!lineLogId) return;
    
    try {
      const { data, error } = await supabase
        .from('production_line_log_supplies')
        .select('*')
        .eq('line_log_id', lineLogId)
        .order('usage_order', { ascending: true });

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading supplies:', error);
        return;
      }

      if (data) {
        setSupplies(data);
      } else {
        setSupplies([]);
      }
    } catch (error) {
      console.error('Error loading supplies:', error);
    }
  };

  const loadAreaChecklist = async (lineLogId) => {
    if (!lineLogId) return;
    
    try {
      const { data, error } = await supabase
        .from('production_line_log_checklists')
        .select('*')
        .eq('line_log_id', lineLogId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading area checklist:', error);
        return;
      }

      if (data) {
        setAreaChecklist(data);
      }
    } catch (error) {
      console.error('Error loading area checklist:', error);
    }
  };

  // Check completion status based on actual data
  useEffect(() => {
    // Check overview completion based on all required fields
    const primaryOperator = operators.find(op => op.is_primary);
    const hasValidOperator = primaryOperator && primaryOperator.operator_name && primaryOperator.operator_name.trim();
    
    // Overview is complete when marked complete in database (this persists on refresh)
    const overviewComplete = lineLog?.overview_completed === true;
    
    // Check if area checklist is locally completed (for real-time updates)
    const areaChecklistLocallyComplete = areaChecklist?.checklist_status === 'complete' || areaChecklist?.all_items_checked === true;
    
    // Check yield summary completion from the new table (only if lineLog.id exists)
    if (lineLog?.id) {
      checkYieldSummaryCompletion();
    }
    
    // Check signature completion - all 3 required roles must be signed
    const requiredSignatureRoles = ['Production Supervisor', 'Quality Control', 'Final Release'];
    const signedRequiredRoles = signatures.filter(sig => requiredSignatureRoles.includes(sig.role));
    const signatureLogComplete = signedRequiredRoles.length === requiredSignatureRoles.length;
    
    const stepChecks = {
      overview: overviewComplete,
      areaChecklist: lineLog?.area_checklist_completed || areaChecklistLocallyComplete || false,
      supplyLog: lineLog?.supply_log_completed || false,
      summaryLog: yieldSummaryCompleted || false,
      signatureLog: lineLog?.signature_log_completed || signatureLogComplete
    };
    
    const completedSteps = Object.values(stepChecks).filter(step => step === true).length;
    const totalSteps = Object.keys(stepChecks).length;
    const progressPercentage = Math.round((completedSteps / totalSteps) * 100);
    
    setWorkflowProgress(progressPercentage);
    setCompletionStatus(stepChecks);
    
    // Check if all steps are completed and report to parent
    const allStepsCompleted = Object.values(stepChecks).every(status => status === true);
    if (onProductionCompletion && typeof onProductionCompletion === 'function') {
      onProductionCompletion(allStepsCompleted, progressPercentage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, lineLog?.id, lineLog?.overview_completed, lineLog?.area_checklist_completed, lineLog?.supply_log_completed, lineLog?.signature_log_completed, operators, areaChecklist, signatures, onProductionCompletion]);
  
  // Separate effect to check yield completion when lineLog.id changes (prevents infinite loop)
  useEffect(() => {
    if (lineLog?.id) {
      checkYieldSummaryCompletion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineLog?.id]);

  // Auto-update signature log completion when signatures change
  useEffect(() => {
    const requiredSignatureRoles = ['Production Supervisor', 'Quality Control', 'Final Release'];
    const signedRequiredRoles = signatures.filter(sig => requiredSignatureRoles.includes(sig.role));
    const isComplete = signedRequiredRoles.length === requiredSignatureRoles.length;
    
    // Only update if completion status has changed
    const currentStatus = lineLog?.signature_log_completed || false;
    if (isComplete !== currentStatus && lineLog?.id) {
      handleSignatureLogComplete(isComplete);
    }
  }, [signatures, lineLog?.id, lineLog?.signature_log_completed]);

  const loadProductionLineLogData = async () => {
    setLoading(true);
    try {
      const { data: logData, error: logError } = await supabase
        .from('production_line_logs')
        .select('*')
        .eq('line_production_id', lineProductionId)
        .single();

      if (logError && logError.code !== 'PGRST116') {
        console.error('Error loading production line log:', logError);
        if (logError.message.includes('406')) {
          console.error('Table may not exist or RLS policy blocking access');
        }
        return;
      }

      if (logData) {
        setLineLog(logData);
        // Load operators for this log
        loadOperators(logData.id);
        // Load area checklist for this log
        loadAreaChecklist(logData.id);
      } 
    } catch (error) {
      console.error('Error loading production line log:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLineItems = async () => {
    try {
      // Load basic line items first
      const { data: itemsData, error: itemsError } = await supabase
        .from('production_line_items')
        .select('*')
        .eq('line_production_id', lineProductionId);

      if (itemsError) throw itemsError;

      // Now load the related item details for each item based on item_type
      const enrichedItems = await Promise.all((itemsData || []).map(async (item) => {
        let itemDetails = null;
        
        try {
          if (item.item_type === 'bottle' || item.item_type === 'bottles') {
            const { data: bottleData } = await supabase
              .from('bottles')
              .select('id, name, sku, capacity, capacity_unit, color, material, supplier:supplier_id(name)')
              .eq('id', item.item_id)
              .single();
            itemDetails = { bottle: bottleData, bottles: bottleData };
          } else if (item.item_type === 'cap' || item.item_type === 'caps') {
            const { data: capData } = await supabase
              .from('caps')
              .select('id, name, sku, closure_type, color, material, supplier:supplier_id(name)')
              .eq('id', item.item_id)
              .single();
            itemDetails = { cap: capData, caps: capData };
          } else if (item.item_type === 'label' || item.item_type === 'labels') {
            const { data: labelData } = await supabase
              .from('labels')
              .select('id, name, sku, material, finish, supplier:supplier_id(name)')
              .eq('id', item.item_id)
              .single();
            itemDetails = { label: labelData, labels: labelData };
          } else if (item.item_type === 'supply' || item.item_type === 'supplies') {
            const { data: supplyData } = await supabase
              .from('supplies')
              .select('id, name, sku, unit, attribute_1_label, attribute_1_value, supplier:supplier_id(name)')
              .eq('id', item.item_id)
              .single();
            itemDetails = { supply: supplyData, supplies: supplyData };
          }
        } catch (detailError) {
          console.error(`Error loading ${item.item_type} details:`, detailError);
        }
        
        return { ...item, ...itemDetails };
      }));
      
      setLineItems(enrichedItems);
      
    } catch (error) {
      console.error('Error loading line items:', error);
    }
  };

  const createProductionLineLog = async () => {
    if (!lineLog) {
      try {
        setError(null); // Clear previous errors
        
        const { data: profile, error: profileError } = await supabase.auth.getUser();
        if (profileError) {
          const errorMsg = 'Authentication error. Please log in again.';
          console.error('Auth error:', profileError);
          setError(errorMsg);
          return null;
        }

        if (!profile?.user?.id) {
          const errorMsg = 'No user session found. Please log in.';
          console.error('No user found');
          setError(errorMsg);
          return null;
        }

        // Try to get company by ownership first, then by membership
        let { data: userCompany, error: companyError } = await supabase
          .from('companies')
          .select('id')
          .eq('created_by', profile.user.id)
          .single();

        // If user is not company owner, check if they're a company user
        if (companyError && companyError.code === 'PGRST116') {
          const { data: companyUser, error: memberError } = await supabase
            .from('company_users')
            .select('company_id, companies!inner(id)')
            .eq('user_id', profile.user.id)
            .eq('status', 'active')
            .single();

          if (memberError) {
            companyError = memberError;
          } else if (companyUser) {
            userCompany = { id: companyUser.company_id };
            companyError = null;
          }
        }

        if (companyError) {
          const errorMsg = companyError.code === 'PGRST116' 
            ? 'No company found for your account. Please create a company first.' 
            : `Database error: ${companyError.message}`;
          console.error('Company error:', companyError);
          setError(errorMsg);
          return null;
        }

        if (!userCompany?.id) {
          const errorMsg = 'No company found for your account. Please create a company first.';
          console.error('No company found for user');
          setError(errorMsg);
          return null;
        }

        const { data: newLog, error } = await supabase
          .from('production_line_logs')
          .insert({
            line_production_id: lineProductionId,
            company_id: userCompany.id,
            start_time: new Date().toISOString()
          })
          .select()
          .single();

        if (error) {
          const errorMsg = error.code === '42P01' 
            ? 'Production line logs table does not exist. Please run the SQL script to create tables.' 
            : `Database error: ${error.message}`;
          console.error('Insert error:', error);
          setError(errorMsg);
          return null;
        }
        
        setLineLog(newLog);
        setError(null); // Clear error on success
        return newLog.id;
      } catch (error) {
        const errorMsg = 'Unexpected error creating production line log. Check console for details.';
        console.error('Error creating production line log:', error);
        setError(errorMsg);
        return null;
      }
    }
    return lineLog.id;
  };

  // Function for AreaCheckList to create/get line log
  const createLineLogForChecklist = async () => {
    return await createProductionLineLog();
  };

  // Function to refresh line log data after checklist updates
  const refreshLineLogData = async () => {
    if (lineLog?.id) {
      try {
        const { data: updatedLog, error } = await supabase
          .from('production_line_logs')
          .select('*')
          .eq('id', lineLog.id)
          .single();

        if (error) {
          console.error('Error refreshing line log:', error);
          return;
        }

        if (updatedLog) {
          setLineLog(updatedLog);
        }
      } catch (error) {
        console.error('Error refreshing line log:', error);
      }
    }
  };

  // Function to refresh supplies data
  const refreshSuppliesData = async () => {
    if (lineLog?.id) {
      await loadSupplies(lineLog.id);
    }
  };

  // Function to check yield summary completion from new table
  const checkYieldSummaryCompletion = async () => {
    if (!lineLog?.id) return;
    
    // Prevent concurrent calls
    if (checkingYieldRef.current) return;
    checkingYieldRef.current = true;

    try {
      const { data, error } = await supabase
        .from('production_line_log_yield')
        .select('total_units_produced, production_end_time, yield_summary_completed')
        .eq('line_log_id', lineLog.id)
        .maybeSingle(); // Use maybeSingle() instead of single() to handle no record gracefully

      // Handle errors - 406 (Not Acceptable) usually means table doesn't exist or RLS blocking
      if (error) {
        // PGRST116 = no rows returned (this is fine, just means no yield data yet)
        // 406 = Not Acceptable (table might not exist or RLS issue)
        if (error.code === 'PGRST116') {
          // No record found - this is expected if yield hasn't been filled out yet
          setYieldSummaryCompleted(false);
          return;
        } else if (error.code === '406' || error.message?.includes('406') || error.status === 406) {
          // Table might not exist or RLS blocking - silently fail to avoid spam
          // Only log once to avoid console spam
          if (!yieldWarningShownRef.current) {
            console.warn('Yield summary table not accessible (406). This may indicate the table does not exist or RLS policies are blocking access.');
            yieldWarningShownRef.current = true;
          }
          setYieldSummaryCompleted(false);
          return;
        } else {
          // Other errors - log but don't spam
          console.error('Error checking yield summary completion:', error);
          setYieldSummaryCompleted(false);
          return;
        }
      }

      // Check if yield data is actually filled out (not just the boolean flag)
      const isActuallyComplete = data && 
        data.total_units_produced !== null && 
        data.total_units_produced > 0 && 
        data.production_end_time !== null;

      setYieldSummaryCompleted(isActuallyComplete);

      // Update the lineLog state to reflect yield completion (only if it changed)
      setLineLog(prev => {
        if (prev?.summary_log_completed !== isActuallyComplete) {
          return {
            ...prev,
            summary_log_completed: isActuallyComplete
          };
        }
        return prev;
      });
    } catch (error) {
      // Catch any unexpected errors
      console.error('Unexpected error checking yield summary completion:', error);
      setYieldSummaryCompleted(false);
    } finally {
      checkingYieldRef.current = false;
    }
  };

  // Function to handle supply log completion
  const handleSupplyLogComplete = async (isComplete) => {
    if (!lineLog?.id) return;

    try {
      // Get the primary operator name for completed_by field
      const primaryOperator = operators.find(op => op.is_primary);
      const completedBy = primaryOperator?.operator_name || 'System';

      const { error } = await supabase
        .from('production_line_logs')
        .update({
          supply_log_completed: isComplete,
          supply_log_completed_at: isComplete ? new Date().toISOString() : null,
          supply_log_completed_by: isComplete ? completedBy : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', lineLog.id);

      if (error) {
        console.error('Error updating supply log completion:', error);
        return;
      }

      // Update local state
      setLineLog(prev => ({
        ...prev,
        supply_log_completed: isComplete,
        supply_log_completed_at: isComplete ? new Date().toISOString() : null,
        supply_log_completed_by: isComplete ? completedBy : null
      }));
    } catch (error) {
      console.error('Error updating supply log completion:', error);
    }
  };

  // Function to handle signature log completion
  const handleSignatureLogComplete = async (isComplete) => {
    if (!lineLog?.id) return;

    try {
      // Get the primary operator name for completed_by field
      const primaryOperator = operators.find(op => op.is_primary);
      const completedBy = primaryOperator?.operator_name || 'System';

      const { error } = await supabase
        .from('production_line_logs')
        .update({
          signature_log_completed: isComplete,
          signature_log_completed_at: isComplete ? new Date().toISOString() : null,
          signature_log_completed_by: isComplete ? completedBy : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', lineLog.id);

      if (error) {
        console.error('Error updating signature log completion:', error);
        return;
      }

      // Update local state
      setLineLog(prev => ({
        ...prev,
        signature_log_completed: isComplete,
        signature_log_completed_at: isComplete ? new Date().toISOString() : null,
        signature_log_completed_by: isComplete ? completedBy : null
      }));
    } catch (error) {
      console.error('Error updating signature log completion:', error);
    }
  };

  const handleTabChange = (newTabValue) => {
    setTabValue(newTabValue);
    
    if (lineLogsRef.current) {
      lineLogsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Define all available steps
  const allSteps = [
    { 
      id: 0, 
      label: 'Overview', 
      icon: <AssignmentIcon />,
      completed: canViewOverview ? (completionStatus.overview || false) : false,
      hasAccess: canViewOverview
    },
    { 
      id: 1, 
      label: 'Area Checklist', 
      icon: <CheckCircleIcon />,
      completed: completionStatus.areaChecklist || false,
      hasAccess: canViewAreaChecklist
    },
    { 
      id: 2, 
      label: 'Supply Log', 
      icon: <Inventory2Icon />,
      completed: completionStatus.supplyLog || false,
      hasAccess: canViewSupplyLog
    },
    { 
      id: 3, 
      label: 'Yield Summary', 
      icon: <TrendingUpIcon />,
      completed: completionStatus.summaryLog || false,
      hasAccess: canViewYieldLog
    },
    { 
      id: 4, 
      label: 'Signatures', 
      icon: <PersonIcon />,
      completed: completionStatus.signatureLog || false,
      hasAccess: canViewSignatures
    }
  ];

  // Filter steps based on permissions
  const availableSteps = allSteps.filter(step => step.hasAccess);
  
  // Check if user has access to any components
  const hasAnyAccess = availableSteps.length > 0;
  
  // Auto-set active tab to first available tab if current tab is not accessible
  useEffect(() => {
    if (availableSteps.length > 0) {
      const currentStepHasAccess = availableSteps.some(step => step.id === tabValue);
      if (!currentStepHasAccess) {
        setTabValue(availableSteps[0].id);
      }
    }
  }, [availableSteps.length, tabValue]);

  // Handle tab change with filtered steps
  const handleTabChangeFiltered = (newTabValue) => {
    // Find the step in available steps to ensure it's accessible
    const targetStep = availableSteps.find(step => step.id === newTabValue);
    if (targetStep) {
      setTabValue(newTabValue);
      
      if (lineLogsRef.current) {
        lineLogsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Show access denied message if no components are accessible
  if (!hasAnyAccess) {
    return (
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100vw',
        position: 'absolute',
        top: 0,
        left: 0,
        backgroundColor: '#f5f5f5'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          textAlign: 'center',
          width: '100%',
          maxWidth: 600,
          px: 3
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            mx: 'auto',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Production Log Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 4,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't have permission to access any production log components. 
              Contact your administrator to request access to:
            </Typography>
            <Box sx={{
              textAlign: 'left',
              maxWidth: 400,
              mx: 'auto',
              mb: 6,
              p: 3,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.grey[100], 0.5),
              border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
            }}>
              <Typography variant="body2" sx={{ 
                fontWeight: 600,
                mb: 1,
                fontSize: '0.9rem'
              }}>
                Production Log Components:
              </Typography>
              <Typography variant="body2" sx={{ 
                ml: 2,
                mb: 0.5,
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                • Overview
              </Typography>
              <Typography variant="body2" sx={{ 
                ml: 2,
                mb: 0.5,
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                • Area Checklist
              </Typography>
              <Typography variant="body2" sx={{ 
                ml: 2,
                mb: 0.5,
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                • Supply Log
              </Typography>
              <Typography variant="body2" sx={{ 
                ml: 2,
                mb: 0.5,
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                • Yield Summary
              </Typography>
              <Typography variant="body2" sx={{ 
                ml: 2,
                fontSize: '0.85rem',
                fontWeight: 500
              }}>
                • Signatures
              </Typography>
            </Box>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box ref={lineLogsRef} sx={{ 
        p: 2,
        pl: 3,
        pr: 3,
        maxWidth: '98vw',
        width: '100%'
      }}>
        {/* Header with Export Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              backdropFilter: 'blur(10px)'
            }}>
              <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 500, mb: 0.5, color: theme.palette.text.primary }}>
                Production Line Log
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Line production documentation
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ 
              mb: 4,
              borderRadius: 2,
              border: `1px solid ${theme.palette.error.main}`,
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              fontWeight: 500
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
              {error}
            </Typography>
          </Alert>
        )}

        <Box sx={{ 
          display: 'flex', 
          gap: 0, 
          borderRadius: 4, 
          backgroundColor: 'white', 
          border: `1px solid ${theme.palette.grey[400]}`, 
          overflow: 'hidden',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          {/* Sidebar Navigation */}
          <Box sx={{ 
            width: 280, 
            bgcolor: `linear-gradient(180deg, ${alpha(theme.palette.grey[50], 0.8)}, ${alpha(theme.palette.grey[100], 0.9)})`, 
            borderRight: `1px solid ${theme.palette.grey[400]}`,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '80vh',
            backdropFilter: 'blur(10px)'
          }}>
            <ProcessNavigation 
              currentStep={tabValue}
              onStepChange={handleTabChangeFiltered}
              scrollRef={lineLogsRef}
              workflowProgress={workflowProgress}
              steps={availableSteps}
            />
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flex: 1, minHeight: '80vh' }}>
            <TabPanel value={tabValue} index={0}>
              {canViewOverview ? (
                <ProductionLineOverview
                  lineLog={lineLog}
                  setLineLog={setLineLog}
                  lineProductionId={lineProductionId}
                  lineProductionData={lineProductionData}
                  createProductionLineLog={createProductionLineLog}
                  lineTimeFormat={lineTimeFormat}
                  operators={operators}
                  setOperators={setOperators}
                  loadOperators={loadOperators}
                  canEdit={hasPermission('production.line.log.overview.edit', 'write')}
                />
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  minHeight: '60vh',
                  textAlign: 'center',
                  width: '100%',
                  px: 3,
                  py: 8
                }}>
                  <Card sx={{
                    p: 6,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 500,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                      opacity: 0.8,
                      transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                      opacity: 1
                    }
                  }}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 3, 
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      mb: 4,
                      display: 'inline-flex',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                      <AssignmentIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h4" sx={{ 
                      mb: 2, 
                      fontWeight: 500, 
                      color: 'text.primary',
                      fontSize: '2rem',
                      letterSpacing: '-0.5px'
                    }}>
                      Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      color: 'text.secondary', 
                      mb: 3,
                      fontWeight: 500,
                      fontSize: '1.1rem'
                    }}>
                      Overview Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      mb: 4,
                      fontSize: '1rem',
                      maxWidth: 400,
                      mx: 'auto'
                    }}>
                      You don't have permission to view the Production Line Overview. 
                      Contact your administrator to request access.
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'text.secondary',
                      fontStyle: 'italic'
                    }}>
                    </Typography>
                  </Card>
                </Box>
              )}
              <NavigationFooter 
                currentStep={tabValue}
                totalSteps={availableSteps.length}
                onStepChange={handleTabChangeFiltered}
                nextStepLabel="Start Area Checklist"
                scrollRef={lineLogsRef}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {canViewAreaChecklist ? (
                <>
                  <AreaCheckList 
                    checklist={areaChecklist}
                    setChecklist={setAreaChecklist}
                    lineLogId={lineLog?.id}
                    createLineLog={createLineLogForChecklist}
                    onChecklistUpdate={refreshLineLogData}
                    lineTimeFormat={lineTimeFormat}
                  />
                  <NavigationFooter 
                    currentStep={tabValue}
                    totalSteps={availableSteps.length}
                    onStepChange={handleTabChangeFiltered}
                    nextStepLabel="Continue to Supply Log"
                    scrollRef={lineLogsRef}
                  />
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No access to Area Checklist
                  </Typography>
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {canViewSupplyLog ? (
                <>
                  <SupplyLog 
                    supplies={supplies}
                    setSupplies={setSupplies}
                    lineLogId={lineLog?.id}
                    lineProductionData={lineProductionData}
                    lineItems={lineItems}
                    onSuppliesUpdate={refreshSuppliesData}
                    onSupplyLogComplete={handleSupplyLogComplete}
                    showSnackbar={(message, severity) => {
                    }}
                    lineTimeFormat={lineTimeFormat}
                  />
                  <NavigationFooter 
                    currentStep={tabValue}
                    totalSteps={availableSteps.length}
                    onStepChange={handleTabChangeFiltered}
                    nextStepLabel="Complete Yield Summary"
                    scrollRef={lineLogsRef}
                  />
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No access to Supply Log
                  </Typography>
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              {canViewYieldLog ? (
                <>
                  <YieldLog
                    lineLogId={lineLog?.id}
                    lineProductionId={lineProductionId}
                    lineTimeFormat={lineTimeFormat}
                    lineProductionData={lineProductionData}
                  />
                  <NavigationFooter 
                    currentStep={tabValue}
                    totalSteps={availableSteps.length}
                    onStepChange={handleTabChangeFiltered}
                    nextStepLabel="Final Signatures"
                    scrollRef={lineLogsRef}
                  />
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No access to Yield Summary
                  </Typography>
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              {canViewSignatures ? (
                <>
                  <SignatureLog 
                    signatures={signatures}
                    setSignatures={setSignatures}
                    lineProductionId={lineProductionId}
                    lineTimeFormat={lineTimeFormat}
                    onDataUpdate={() => {
                      // The useEffect above will handle completion tracking automatically
                      // This callback is just for any additional actions needed after signature updates
                      console.log('Signature data updated');
                    }}
                  />
                  <NavigationFooter 
                    currentStep={tabValue}
                    totalSteps={availableSteps.length}
                    onStepChange={handleTabChangeFiltered}
                    nextStepLabel="Complete Production"
                    scrollRef={lineLogsRef}
                    allStepsCompleted={Object.values(completionStatus).every(status => status === true)}
                  />
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary">
                    No access to Signatures
                  </Typography>
                </Box>
              )}
            </TabPanel>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

// Production Line Overview Component
function ProductionLineOverview({
  lineLog,
  setLineLog,
  lineProductionId,
  lineProductionData,
  createProductionLineLog,
  lineTimeFormat = '12hour',
  operators,
  setOperators,
  loadOperators,
  canEdit
}) {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    production_location: '',
    production_line: '',
    equipment_id: '',
    start_time: null,
    temperature_value: '',
    temperature_unit: 'F',
    humidity_percent: '',
    cleaning_status_confirmed: false,
    cleaning_signature: '',
    cleaning_confirmed_at: '',
    general_notes: ''
  });

  const [cleaningSignatureDialog, setCleaningSignatureDialog] = useState(false);
  const [cleaningPreviewName, setCleaningPreviewName] = useState('');
  const [errors, setErrors] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [locationSelectionDialogOpen, setLocationSelectionDialogOpen] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const justSavedRef = useRef(false);

  // Initialize form data from lineLog, but respect user edits and saves
  const hasInitializedRef = useRef(false);
  
  useEffect(() => {
    // Check if current location value is still an unresolved UUID (locations not loaded yet)
    const currentLocationIsUuid = formData.production_location && formData.production_location.length > 20 && formData.production_location.includes('-');
    // Initialize when lineLog loads (existing or null=new) or when lineProductionData arrives
    // Also re-run if location was stored as UUID but locations list has now loaded
    if (!hasInitializedRef.current || justSavedRef.current || currentLocationIsUuid) {
      // Auto-fill start_time: prefer saved value, fall back to lineProductionData planned time
      let startTime = null;
      if (lineLog?.start_time) {
        startTime = dayjs(lineLog.start_time);
      } else if (lineProductionData?.planned_start_date && lineProductionData?.planned_start_time) {
        startTime = dayjs(`${lineProductionData.planned_start_date} ${lineProductionData.planned_start_time}`, 'YYYY-MM-DD HH:mm');
      }

      // production_location in line_productions is a UUID — resolve to name for the form
      const rawLocation = lineLog?.production_location || lineProductionData?.production_location || '';
      const isUuid = rawLocation && rawLocation.length > 20 && rawLocation.includes('-');
      const productionLocation = isUuid
        ? (locations.find(l => l.id === rawLocation)?.name || rawLocation)
        : rawLocation;
      const productionLine = lineLog?.production_line || lineProductionData?.production_line || '';

      // Only set if we have something meaningful (don't blank out a partially-loaded form)
      if (productionLocation || productionLine || startTime || lineLog) {
        setFormData({
          production_location: productionLocation,
          production_line: productionLine,
          equipment_id: lineLog?.equipment_id || '',
          start_time: startTime,
          temperature_value: lineLog?.temperature_value || '',
          temperature_unit: lineLog?.temperature_unit || 'F',
          humidity_percent: lineLog?.humidity_percent || '',
          cleaning_status_confirmed: lineLog?.cleaning_status_confirmed || false,
          cleaning_signature: lineLog?.cleaning_signature || '',
          cleaning_confirmed_at: lineLog?.cleaning_confirmed_at || '',
          general_notes: lineLog?.general_notes || ''
        });
        hasInitializedRef.current = true;

        // Clear the justSaved flag after updating form
        if (justSavedRef.current) {
          setTimeout(() => {
            justSavedRef.current = false;
          }, 100);
        }
      }
    }
  }, [lineLog, lineProductionData, locations]);

  useEffect(() => {
    loadLocations();
  }, []); // Only run once on mount

  useEffect(() => {
    // Initialize preview with current cleaning signature when dialog opens
    if (cleaningSignatureDialog) {
      setCleaningPreviewName(formData.cleaning_signature || '');
    }
  }, [cleaningSignatureDialog]); // Removed formData.cleaning_signature dependency


  const loadLocations = async () => {
    try {
      setLoadingLocations(true);
      
      const { data, error } = await supabase
        .from('locations')
        .select('id, name, city, state, facility_type, is_main')
        .eq('is_active', true)
        .order('is_main', { ascending: false })
        .order('name');

      if (error) {
        console.error('Error loading locations:', error);
        return;
      }

      // Sort locations to ensure main location appears first
      const locationsList = (data || []).sort((a, b) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      
      setLocations(locationsList);
      

    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Operator management functions are now passed as props

  const addOperator = () => {
    const newOperator = {
      id: null,
      operator_name: '',
      operator_signature: '',
      operator_role: 'Operator',
      is_primary: false,
      shift_start_time: dayjs(),
      shift_end_time: null,
      is_active: true
    };
    setOperators([...operators, newOperator]);
  };

  const removeOperator = (index) => {
    if (operators.length <= 1) return; // Keep at least one operator
    const newOperators = operators.filter((_, i) => i !== index);
    // If we removed the primary operator, make the first one primary
    if (operators[index].is_primary && newOperators.length > 0) {
      newOperators[0].is_primary = true;
    }
    setOperators(newOperators);
  };

  const updateOperator = (index, field, value) => {
    const newOperators = [...operators];
    newOperators[index] = { ...newOperators[index], [field]: value };
    
    // If setting as primary, unset others
    if (field === 'is_primary' && value === true) {
      newOperators.forEach((op, i) => {
        if (i !== index) op.is_primary = false;
      });
    }
    
    setOperators(newOperators);
  };

  const saveOperators = async (lineLogId) => {
    try {
      // Get company_id from lineLog
      const companyId = lineLog?.company_id;
      if (!companyId) {
        console.error('No company ID found');
        return;
      }

      // Remove existing operators for this log
      const { error: deleteError } = await supabase
        .from('production_line_operators')
        .delete()
        .eq('line_log_id', lineLogId);

      if (deleteError) {
        console.error('Error deleting existing operators:', deleteError);
        return;
      }

      // Insert new operators (only those with names)
      const operatorsToSave = operators
        .filter(op => op.operator_name && op.operator_name.trim())
        .map(op => ({
          line_log_id: lineLogId,
          company_id: companyId,
          operator_name: op.operator_name.trim(),
          operator_signature: op.operator_signature || null,
          operator_role: op.operator_role || 'Operator',
          is_primary: op.is_primary || false,
          shift_start_time: op.shift_start_time ? op.shift_start_time.toISOString() : null,
          shift_end_time: op.shift_end_time ? op.shift_end_time.toISOString() : null,
          is_active: true
        }));

      if (operatorsToSave.length > 0) {
        const { error: insertError } = await supabase
          .from('production_line_operators')
          .insert(operatorsToSave);

        if (insertError) {
          console.error('Error saving operators:', insertError);
          return;
        }
      }
    } catch (error) {
      console.error('Error in saveOperators:', error);
    }
  };

  // Location dialog handlers
  const handleOpenLocationSelection = () => {
    setLocationSearchTerm('');
    setLocationSelectionDialogOpen(true);
  };

  const handleCloseLocationSelection = () => {
    setLocationSelectionDialogOpen(false);
    setLocationSearchTerm('');
  };

  const handleFormDataChange = (newData) => {
    justSavedRef.current = false; // Clear save flag when user makes changes
    hasInitializedRef.current = true; // Mark as initialized after first user edit
    setFormData(newData);
  };

  const handleSelectLocation = async (location) => {
    handleFormDataChange({ ...formData, production_location: location.name });
    handleCloseLocationSelection();
  };

  const getFilteredLocations = () => {
    const searchTerm = locationSearchTerm.toLowerCase();
    const filtered = !searchTerm.trim() 
      ? locations 
      : locations.filter(location => 
        location.name?.toLowerCase().includes(searchTerm) ||
        location.facility_type?.toLowerCase().includes(searchTerm) ||
        location.city?.toLowerCase().includes(searchTerm) ||
        location.state?.toLowerCase().includes(searchTerm)
      );
    
    // Maintain sort order: main location first, then alphabetical
    return filtered.sort((a, b) => {
      if (a.is_main && !b.is_main) return -1;
      if (!a.is_main && b.is_main) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Check if at least one operator has a name
    const validOperators = operators.filter(op => op.operator_name && op.operator_name.trim());
    if (validOperators.length === 0) {
      newErrors.operators = 'At least one operator name is required';
    }
    
    // Check if there's exactly one primary operator
    const primaryOperators = operators.filter(op => op.is_primary);
    if (primaryOperators.length === 0) {
      newErrors.primary_operator = 'One operator must be marked as primary';
    } else if (primaryOperators.length > 1) {
      newErrors.primary_operator = 'Only one operator can be marked as primary';
    }
    
    if (!formData.production_location.trim()) {
      newErrors.production_location = 'Production location is required';
    }
    
    if (!formData.production_line.trim()) {
      newErrors.production_line = 'Production line is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Check permissions before proceeding
    if (!canEdit) {
      setErrors({ submit: 'You do not have permission to save production line overview.' });
      return;
    }

    try {
      setSaveSuccess(false);
      setErrors({});
      
      if (!validateForm()) {
        return;
      }
      
      const lineLogId = await createProductionLineLog();
      if (!lineLogId) {
        setErrors({ submit: 'Failed to create or get production line log ID' });
        return;
      }

      // Check if all required fields are completed for overview
      const primaryOperator = operators.find(op => op.is_primary);
      const hasValidOperator = primaryOperator && primaryOperator.operator_name && primaryOperator.operator_name.trim();
      
      const allRequiredFieldsComplete = !!(
        hasValidOperator &&
        formData.production_location && 
        formData.production_line &&
        formData.start_time
      );
      
      // Only mark as complete if all fields are complete, otherwise mark as incomplete
      const overviewIsComplete = allRequiredFieldsComplete;

      const updateData = {
        ...formData,
        temperature_value: formData.temperature_value !== '' ? formData.temperature_value : null,
        humidity_percent: formData.humidity_percent !== '' ? formData.humidity_percent : null,
        start_time: formData.start_time?.toISOString(),
        total_duration_minutes: formData.start_time 
          ? Math.round(dayjs().diff(formData.start_time, 'minute'))
          : null,
        // Mark overview as complete/incomplete based on field completion
        overview_completed: overviewIsComplete,
        overview_completed_at: overviewIsComplete ? dayjs().toISOString() : null,
        overview_completed_by: overviewIsComplete ? primaryOperator?.operator_name : null,
        updated_at: dayjs().toISOString()
      };

      const { data, error } = await supabase
        .from('production_line_logs')
        .update(updateData)
        .eq('id', lineLogId)
        .select()
        .single();

      if (error) {
        console.error('Error updating production line log:', error);
        setErrors({ submit: `Database error: ${error.message}` });
        return;
      }
      
      // Save operators
      await saveOperators(lineLogId);
      
      // Set flag to prevent form reset, then reload the line log data
      justSavedRef.current = true;
      setLineLog(data);
      setSaveSuccess(true);
      
      // Clear the flags after a short delay
      setTimeout(() => {
        setSaveSuccess(false);
        justSavedRef.current = false;
      }, 3000);
    } catch (error) {
      console.error('Error saving production line overview:', error);
      setErrors({ submit: 'Unexpected error occurred while saving' });
    }
  };

  const handleCleaningSignature = () => {
    const signatureName = document.getElementById('cleaning-signature-name')?.value || '';
    
    if (!signatureName.trim()) {
      return;
    }
    
    handleFormDataChange({
      ...formData,
      cleaning_signature: signatureName,
      cleaning_status_confirmed: true,
      cleaning_confirmed_at: dayjs().toISOString()
    });
    
    setCleaningSignatureDialog(false);
  };

  const getDurationHours = () => {
    if (formData.start_time) {
      return dayjs().diff(formData.start_time, 'hour', true);
    }
    return 0;
  };

  return (
    <Box>
      {/* Error Messages */}
      {Object.keys(errors).length > 0 && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4,
            borderRadius: 2,
            border: `1px solid ${theme.palette.error.main}`,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            fontWeight: 500
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: 'black' }}>
            Please fix the following errors:
          </Typography>
          {Object.entries(errors).map(([field, message]) => (
            <Typography key={field} variant="body2" sx={{ display: 'block', color: 'black' }}>
              • {message}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Success Message */}
      {saveSuccess && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 4,
            borderRadius: 2,
            border: `1px solid ${theme.palette.success.main}`,
            backgroundColor: alpha(theme.palette.success.main, 0.1),
            fontWeight: 500
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            Production line overview saved successfully!
          </Typography>
        </Alert>
      )}

      {/* Dashboard Cards */}
      <Box sx={{ mb: 6 }}>
        <Grid container spacing={3}>
          {/* Operators Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              borderRadius: 4,
              border: `1px solid ${theme.palette.grey[400]}`,
              backgroundColor: 'white',
              height: 220,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                zIndex: 10
              }
            }}>
              <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{
                  width: 45,
                  height: 45,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                }}>
                  <GroupIcon sx={{ fontSize: 24, color: 'white' }} />
                </Box>
                {(() => {
                  const primaryOperator = operators.find(op => op.is_primary);
                  const operatorCount = operators.filter(op => op.operator_name && op.operator_name.trim()).length;
                  return (
                    <>
                <Typography variant="h5" sx={{ 
                  fontWeight: 500, 
                  color: theme.palette.text.primary, 
                  mb: 0.5,
                  textAlign: 'center',
                  wordBreak: 'break-word'
                }}>
                        {primaryOperator?.operator_name || 'No Primary Operator'}
                </Typography>
                <Typography variant="body2" sx={{ 
                  fontWeight: 500, 
                  color: 'text.secondary', 
                  textTransform: 'uppercase',
                        letterSpacing: 1,
                        mb: 0.5
                }}>
                        Primary Operator
                </Typography>
                      {operatorCount > 1 && (
                        <Typography variant="caption" sx={{ 
                          fontWeight: 500, 
                          color: theme.palette.primary.main,
                          fontSize: '0.7rem'
                        }}>
                          +{operatorCount - 1} additional operator{operatorCount > 2 ? 's' : ''}
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          {/* Production Line & Location Cards */}
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: 220 }}>
              <Card sx={{ 
                borderRadius: 3,
                border: formData.production_location ? `1px solid ${theme.palette.success.main}` : `1px solid ${theme.palette.grey[400]}`,
                backgroundColor: formData.production_location ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[100], 0.8),
                flex: 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                }
              }}>
                <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 500, 
                    color: formData.production_location ? theme.palette.success.main : theme.palette.text.disabled, 
                    mb: 0.5,
                    textAlign: 'center',
                    wordBreak: 'break-word'
                  }}>
                                                            {formData.production_location || ''}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    fontWeight: 500, 
                    color: 'text.secondary', 
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    Location
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ 
                borderRadius: 3,
                border: formData.production_line ? `1px solid ${theme.palette.info.main}` : `1px solid ${theme.palette.grey[400]}`,
                backgroundColor: formData.production_line ? alpha(theme.palette.info.main, 0.1) : alpha(theme.palette.grey[100], 0.8),
                flex: 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                }
              }}>
                <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 500, 
                    color: formData.production_line ? theme.palette.info.main : theme.palette.text.disabled, 
                    mb: 0.5,
                    textAlign: 'center',
                    wordBreak: 'break-word'
                  }}>
                                                            {formData.production_line || ''}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    fontWeight: 500, 
                    color: 'text.secondary', 
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    Production Line
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Grid>

          {/* Cleaning Status Card */}
          <Grid item xs={12} md={3}>
            <Card sx={{ 
              borderRadius: 4,
              border: formData.cleaning_status_confirmed ? `1px solid ${theme.palette.success.main}` : `1px solid ${theme.palette.error.main}`,
              backgroundColor: formData.cleaning_status_confirmed ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
              height: 220,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`
              }
            }}>
              <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <CheckCircleIcon sx={{ 
                  fontSize: 36, 
                  color: formData.cleaning_status_confirmed ? theme.palette.success.main : theme.palette.error.main, 
                  mb: 1.5,
                  filter: `drop-shadow(0 4px 8px ${alpha(theme.palette.primary.main, 0.2)})`
                }} />
                <Typography variant="h5" sx={{ 
                  fontWeight: 500, 
                  color: formData.cleaning_status_confirmed ? theme.palette.success.main : theme.palette.error.main,
                  textAlign: 'center',
                  mb: 0.5
                }}>
                  {formData.cleaning_status_confirmed ? 'CLEAN' : 'PENDING'}
                </Typography>
                <Typography variant="caption" sx={{ 
                  fontWeight: 500, 
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  textAlign: 'center'
                }}>
                  Cleaning Status
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Environmental Cards */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              borderRadius: 4,
              border: formData.temperature_value ? `1px solid ${theme.palette.warning.main}` : `1px solid ${theme.palette.grey[400]}`,
              backgroundColor: formData.temperature_value ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.grey[100], 0.8),
              height: 170,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`
              }
            }}>
              <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 500, 
                  color: formData.temperature_value ? theme.palette.warning.dark : theme.palette.text.disabled,
                  mb: 0.5
                }}>
                                                    {formData.temperature_value ? `${formData.temperature_value}°${formData.temperature_unit}` : ''}
                </Typography>
                <ThermostatIcon sx={{ fontSize: 20, color: formData.temperature_value ? theme.palette.warning.dark : theme.palette.text.disabled, mb: 0.5 }} />
                <Typography variant="caption" sx={{ 
                  fontWeight: 500, 
                  color: 'text.secondary', 
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  textAlign: 'center'
                }}>
                  Temperature
                </Typography>
              </CardContent>
            </Card>
          </Grid>


          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 4,
              border: formData.start_time ? `1px solid ${theme.palette.text.primary}` : `1px solid ${theme.palette.grey[400]}`,
              backgroundColor: formData.start_time ? 'white' : alpha(theme.palette.grey[100], 0.8),
              height: 170,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`
              }
            }}>
              <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 500, 
                  color: formData.start_time ? theme.palette.text.primary : theme.palette.text.disabled,
                  mb: 0.5
                }}>
                  {formData.start_time ? `${getDurationHours().toFixed(1)}h` : '0.0h'}
                </Typography>
                <ScheduleIcon sx={{ fontSize: 20, color: formData.start_time ? theme.palette.text.primary : theme.palette.text.disabled, mb: 0.5 }} />
                <Typography variant="caption" sx={{ 
                  fontWeight: 500, 
                  color: 'text.secondary', 
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  textAlign: 'center'
                }}>
                  Elapsed Time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Operator & Location Input Card */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            minHeight: 400, 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  mr: 2
                }}>
                  <PersonIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Production Operators
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Production staff assignments
                  </Typography>
                </Box>
                <Box sx={{ ml: 4 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => canEdit ? addOperator() : null}
                    disabled={!canEdit}
                    sx={{
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 500,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        borderColor: theme.palette.primary.dark,
                        backgroundColor: alpha(theme.palette.primary.main, 0.05)
                      }
                    }}
                  >
                    Add Operator
                  </Button>
                </Box>
                </Box>
              </Box>
              
            <Stack spacing={3} sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 400 }}>
              {operators.map((operator, index) => (
                <Card key={index} sx={{
                  border: operator.is_primary ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  backgroundColor: operator.is_primary ? alpha(theme.palette.primary.main, 0.05) : 'white'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: operator.is_primary ? theme.palette.primary.main : theme.palette.text.secondary 
                      }}>
                        {operator.is_primary ? 'Primary Operator' : `Operator ${index + 1}`}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Checkbox
                          checked={operator.is_primary}
                          onChange={(e) => canEdit ? (justSavedRef.current = false, updateOperator(index, 'is_primary', e.target.checked)) : null}
                          disabled={!canEdit}
                          size="small"
                          sx={{ p: 0.5 }}
                        />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', mt: 0.5 }}>Primary</Typography>
                        {operators.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => canEdit ? removeOperator(index) : null}
                            disabled={!canEdit}
                            sx={{ 
                              ml: 1, 
                              color: theme.palette.error.main,
                              '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                          size="small"
                  label="Operator Name *"
                          value={operator.operator_name}
                          onChange={(e) => canEdit ? (justSavedRef.current = false, updateOperator(index, 'operator_name', e.target.value)) : null}
                          disabled={!canEdit}
                          error={!!errors.operators && !operator.operator_name.trim()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                              '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                              '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                    }
                  }}
                />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Role</InputLabel>
                          <Select
                            value={operator.operator_role}
                            onChange={(e) => canEdit ? (justSavedRef.current = false, updateOperator(index, 'operator_role', e.target.value)) : null}
                            disabled={!canEdit}
                            label="Role"
                  sx={{
                              '& .MuiOutlinedInput-notchedOutline': { 
                                borderColor: theme.palette.grey[400],
                                borderWidth: 1
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': { 
                                borderColor: theme.palette.grey[600],
                                borderWidth: 1
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                                borderColor: theme.palette.grey[700],
                                borderWidth: 1
                              }
                            }}
                          >
                            <MenuItem value="Lead Operator">Lead Operator</MenuItem>
                            <MenuItem value="Operator">Operator</MenuItem>
                            <MenuItem value="Assistant">Assistant</MenuItem>
                            <MenuItem value="Supervisor">Supervisor</MenuItem>
                            <MenuItem value="Trainee">Trainee</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              {/* Error messages for operators */}
              {(errors.operators || errors.primary_operator) && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {errors.operators && <Typography variant="body2">• {errors.operators}</Typography>}
                  {errors.primary_operator && <Typography variant="body2">• {errors.primary_operator}</Typography>}
                                 </Alert>
               )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

      {/* Production Location Card */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            minHeight: 400, 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  mr: 2
                }}>
                <BusinessIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                  Production Location & Line
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Facility location and production line details
                  </Typography>
                </Box>
              </Box>
              
              <Stack spacing={3} sx={{ flexGrow: 1 }}>
                <TextField
                  fullWidth
                  label="Production Location *"
                  value={formData.production_location || ''}
                  onClick={canEdit ? handleOpenLocationSelection : null}
                  placeholder={canEdit ? "Click to select production location" : "Production location (read-only)"}
                  error={!!errors.production_location}
                  helperText={errors.production_location || (canEdit ? 'Click to select from available locations' : 'Read-only field')}
                  disabled={!canEdit}
                  required
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <BusinessIcon sx={{ color: theme.palette.grey[600] }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    cursor: 'pointer',
                    '& .MuiOutlinedInput-root': {
                      cursor: 'pointer',
                      '& fieldset': { borderColor: errors.production_location ? theme.palette.error.main : theme.palette.grey[400], borderWidth: 1 },
                      '&:hover fieldset': { borderColor: errors.production_location ? theme.palette.error.main : theme.palette.grey[600], borderWidth: 1 },
                      '&.Mui-focused fieldset': { borderColor: errors.production_location ? theme.palette.error.main : theme.palette.grey[700], borderWidth: 1 }
                    },
                    '& .MuiInputBase-input': {
                      cursor: 'pointer'
                    }
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Production Line *"
                  value={formData.production_line}
                  onChange={(e) => canEdit ? handleFormDataChange({...formData, production_line: e.target.value}) : null}
                  disabled={!canEdit}
                  error={!!errors.production_line}
                  helperText={errors.production_line}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: errors.production_line ? theme.palette.error.main : theme.palette.grey[400], borderWidth: 1 },
                      '&:hover fieldset': { borderColor: errors.production_line ? theme.palette.error.main : theme.palette.grey[600], borderWidth: 1 },
                      '&.Mui-focused fieldset': { borderColor: errors.production_line ? theme.palette.error.main : theme.palette.grey[700], borderWidth: 1 }
                    }
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Line ID"
                  value={formData.equipment_id}
                  onChange={(e) => canEdit ? handleFormDataChange({...formData, equipment_id: e.target.value}) : null}
                  disabled={!canEdit}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                      '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                      '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                    }
                  }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>


        {/* Timing & Environmental Card */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            height: 450,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                <Box sx={{
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  mr: 2,
                  flexShrink: 0
                }}>
                  <ScheduleIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Timing & Environmental Conditions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Process timing and environmental monitoring
                  </Typography>
                </Box>
              </Box>
              
              <Stack spacing={3} sx={{ flexGrow: 1 }}>
                <DateTimePicker
                  label="Start Time"
                  value={formData.start_time}
                  onChange={(newValue) => canEdit ? handleFormDataChange({...formData, start_time: newValue}) : null}
                  disabled={!canEdit}
                  format={lineTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                  ampm={lineTimeFormat !== '24hour'}
                  renderInput={(params) => <TextField {...params} fullWidth 
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                      }
                    }}
                  />}
                />
                

                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2, alignItems: 'start' }}>
                  <TextField
                    label="Temperature"
                    type="number"
                    value={formData.temperature_value}
                    onChange={(e) => canEdit ? handleFormDataChange({...formData, temperature_value: e.target.value}) : null}
                    disabled={!canEdit}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                      }
                    }}
                  />
                  <FormControl sx={{ width: '100%', minWidth: 80 }}>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={formData.temperature_unit}
                      onChange={(e) => canEdit ? handleFormDataChange({...formData, temperature_unit: e.target.value}) : null}
                      disabled={!canEdit}
                      label="Unit"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: theme.palette.grey[400],
                          borderWidth: 1
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': { 
                          borderColor: theme.palette.grey[600],
                          borderWidth: 1
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                          borderColor: theme.palette.grey[700],
                          borderWidth: 1
                        }
                      }}
                    >
                      <MenuItem value="C">°C</MenuItem>
                      <MenuItem value="F">°F</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Cleaning Status & Notes Card */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            height: 450,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                <Box sx={{
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  mr: 2,
                  flexShrink: 0
                }}>
                  <ScienceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                    Cleaning Status & Notes
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Equipment cleaning verification and general observations
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.cleaning_status_confirmed}
                        onChange={(e) => canEdit ? handleFormDataChange({...formData, cleaning_status_confirmed: e.target.checked}) : null}
                        disabled={!canEdit}
                        sx={{ color: theme.palette.grey[600], '&.Mui-checked': { color: theme.palette.primary.main } }}
                      />
                    }
                    label="Cleaning Status Confirmed"
                    sx={{ fontWeight: 500 }}
                  />
                  
                  <Button 
                    variant="contained" 
                    onClick={() => canEdit ? setCleaningSignatureDialog(true) : null}
                    disabled={!formData.cleaning_status_confirmed || !canEdit}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      color: theme.palette.grey[600],
                      fontWeight: 500,
                      background: alpha(theme.palette.grey[400], 0.3),
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.grey[500], 0.4),
                        borderColor: alpha(theme.palette.grey[500], 0.8)
                      },
                      '&:disabled': {
                        background: alpha(theme.palette.grey[300], 0.5),
                        color: theme.palette.text.disabled
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    {formData.cleaning_signature ? 'Change Signature' : 'Add Signature'}
                  </Button>
                </Box>

                {formData.cleaning_signature && (
                  <Box sx={{ 
                    border: `1px solid ${theme.palette.primary.main}`, 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.primary.main, 0.05)
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                      CLEANING SIGNATURE:
                    </Typography>
                    <Box sx={{ 
                      fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                      fontSize: '20px', 
                      color: theme.palette.text.primary,
                      textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                      mb: 1
                    }}>
                      {formData.cleaning_signature}
                    </Box>
                    {formData.cleaning_confirmed_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Signed: {dayjs(formData.cleaning_confirmed_at).format(lineTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                      </Typography>
                    )}
                  </Box>
                )}
                
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    <TextField
                      fullWidth
                      multiline
                      label="General Notes"
                      value={formData.general_notes}
                      onChange={(e) => canEdit ? handleFormDataChange({...formData, general_notes: e.target.value}) : null}
                      disabled={!canEdit}
                      placeholder={canEdit ? "Enter any additional observations or notes..." : "General notes (read-only)"}
                    sx={{
                      height: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: '100%',
                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                      },
                      '& .MuiOutlinedInput-input': {
                        height: '100% !important',
                        overflow: 'auto !important',
                        resize: 'none'
                      }
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained"
              onClick={handleSave}
              startIcon={<CheckCircleIcon />}
              size="large"
              disabled={!canEdit}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                color: theme.palette.text.secondary,
                fontWeight: 500,
                background: 'rgba(255, 255, 255, 0.95)',
                border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.04)
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Save Overview
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Cleaning Signature Dialog */}
      <Dialog 
        open={cleaningSignatureDialog} 
        onClose={() => setCleaningSignatureDialog(false)} 
        maxWidth="sm" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            border: `1px solid ${theme.palette.grey[400]}`
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: alpha(theme.palette.grey[100], 0.8), 
          borderBottom: `1px solid ${theme.palette.grey[400]}`,
          fontWeight: 500
        }}>
          Add Cleaning Confirmation Signature
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mt: 2, color: theme.palette.grey[700] }}>
                Enter your name to confirm cleaning status:
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                id="cleaning-signature-name"
                label="Name"
                fullWidth
                autoFocus
                required
                defaultValue={formData.cleaning_signature || ''}
                onChange={(e) => setCleaningPreviewName(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                    '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                    '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                mt: 2, 
                p: 3, 
                border: `1px solid ${theme.palette.grey[400]}`, 
                borderRadius: 2, 
                backgroundColor: alpha(theme.palette.grey[50], 0.8)
              }}>
                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                  Preview: Your signature will appear in script font
                </Typography>
                <Box sx={{ 
                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                  fontSize: '28px', 
                  color: theme.palette.grey[800],
                  mt: 2,
                  mb: 1,
                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {cleaningPreviewName || 'Preview will appear here'}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <Button 
            onClick={() => setCleaningSignatureDialog(false)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              color: theme.palette.grey[600],
              '&:hover': {
                backgroundColor: alpha(theme.palette.grey[200], 0.5)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCleaningSignature} 
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1,
              background: alpha(theme.palette.grey[600], 0.9),
              color: 'white',
              fontWeight: 500,
              '&:hover': {
                background: theme.palette.grey[700]
              }
            }}
          >
            Confirm Signature
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Selection Dialog */}
      <Dialog
        open={locationSelectionDialogOpen}
        onClose={handleCloseLocationSelection}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500,
          fontSize: '1.2rem',
          color: 'primary.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 2 }} />
            Select Production Location
          </Box>
          <IconButton 
            onClick={handleCloseLocationSelection} 
            size="small"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                transform: 'scale(1.1)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search locations by name, facility type, city, or state..."
              value={locationSearchTerm}
              onChange={(e) => setLocationSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  fontSize: '0.9rem',
                  '& fieldset': { border: 'none' },
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
            />
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Location
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                  }}>
                    Details
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.9rem',
                    background: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    width: 100
                  }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredLocations().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {locationSearchTerm ? 'No locations found matching your search.' : 'No production locations available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredLocations().map((location) => (
                    <TableRow
                      key={location.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                      }}
                      onClick={() => handleSelectLocation(location)}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                            {location.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {location.facility_type}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {location.city}, {location.state}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectLocation(location);
                          }}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 500,
                            fontSize: '0.8rem',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                            }
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, justifyContent: 'space-between' }}>
          <Button 
            onClick={() => {
              const companyId = lineLog?.company_id;
              window.open(`/company/${companyId}/locations`, '_blank');
            }}
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              fontWeight: 500,
              px: 3,
              py: 1
            }}
          >
            Add Location
          </Button>
          <Button 
            onClick={handleCloseLocationSelection}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              fontWeight: 500,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-1px)',
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProductionLineLogs;